import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, CheckCircle2, Download, Loader2, Play, RefreshCw } from 'lucide-react';

import { adminDataApi, AdminOpsBusinessStatus } from '../../api/adminDataApi';
import { vendorApi } from '../../api/vendorApi';
import DataExportModal from './components/DataExportModal';
import RenormalizeModal, { RenormalizeParams } from './components/RenormalizeModal';
import { useAdminContext } from './AdminContext';
import { formatLocalTimestamp } from '../../utils/formatLocalTimestamp';

const statusClasses = (status?: string | null) => {
    const normalized = String(status || 'idle').toLowerCase();
    if (normalized === 'running') return 'bg-amber-100 text-amber-800';
    if (normalized === 'syncing') return 'bg-blue-100 text-blue-800';
    if (normalized === 'healthy' || normalized === 'clear') return 'bg-green-100 text-green-800';
    if (normalized === 'warning') return 'bg-amber-100 text-amber-800';
    if (normalized === 'queued') return 'bg-blue-100 text-blue-800';
    if (normalized === 'completed' || normalized === 'success' || normalized === 'ready') return 'bg-green-100 text-green-800';
    if (normalized === 'failed' || normalized === 'error') return 'bg-red-100 text-red-800';
    return 'bg-gray-100 text-gray-700';
};

const StatusPill: React.FC<{ label: string; status?: string | null }> = ({ label, status }) => (
    <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${statusClasses(status)}`}>
        {label}: {status || 'idle'}
    </span>
);

const JobList: React.FC<{ jobs: Array<{ job_id: string; job_type: string; status: string; started_at?: string | null; finished_at?: string | null; error?: string | null }> }> = ({ jobs }) => {
    if (!jobs.length) {
        return <div className="text-xs text-gray-400">No recent jobs</div>;
    }

    return (
        <div className="space-y-2">
            {jobs.map((job) => (
                <div key={job.job_id} className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
                    <div className="flex items-center justify-between gap-3">
                        <div className="text-xs font-medium text-gray-900">{job.job_type}</div>
                        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${statusClasses(job.status)}`}>
                            {job.status}
                        </span>
                    </div>
                    <div className="mt-1 text-[11px] text-gray-500">
                        Started {formatLocalTimestamp(job.started_at)}{job.finished_at ? ` · Finished ${formatLocalTimestamp(job.finished_at)}` : ''}
                    </div>
                    {job.error ? <div className="mt-1 text-[11px] text-red-600">{job.error}</div> : null}
                </div>
            ))}
        </div>
    );
};

const BusinessCard: React.FC<{
    item: AdminOpsBusinessStatus;
    busyAction: string | null;
    onSyncQbo: (businessId: string) => void;
    onSyncMappings: (businessId: string) => void;
    onResolveVendors: (businessId: string) => void;
    onRenormalize: (businessId: string) => void;
    onDownload: (item: AdminOpsBusinessStatus) => void;
}> = ({ item, busyAction, onSyncQbo, onSyncMappings, onResolveVendors, onRenormalize, onDownload }) => {
    const totalBacklog = item.vendor_resolution.total_flagged_count;
    const qboStatus = item.qbo_account_mapping.status;
    const connector = item.qbo_connector || {};
    const connectorStatus = connector.status || (item.connections.qbo_linked ? 'no heartbeat' : 'not linked');
    const connectorQueue = Number(connector.queued_payload_count || 0);

    return (
        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div>
                    <div className="flex items-center gap-2">
                        <h2 className="text-lg font-semibold text-gray-900">{item.business_name}</h2>
                        {item.connections.qbo_linked ? (
                            <CheckCircle2 className="h-4 w-4 text-green-600" />
                        ) : (
                            <AlertCircle className="h-4 w-4 text-gray-400" />
                        )}
                    </div>
                    <div className="mt-1 text-sm text-gray-500">{item.business_id}</div>
                    {item.owner_email ? <div className="text-sm text-gray-500">{item.owner_email}</div> : null}
                    <div className="mt-3 flex flex-wrap gap-2">
                        <StatusPill label="QBO Map" status={qboStatus} />
                        <StatusPill label="Exports" status={item.jobs.export_active_count > 0 ? 'running' : 'idle'} />
                        <StatusPill label="ETL" status={item.jobs.etl_active_count > 0 ? 'running' : 'idle'} />
                        <StatusPill label="Vendor Queue" status={totalBacklog > 0 ? 'pending' : 'clear'} />
                    </div>
                </div>

                <div className="flex flex-wrap gap-2">
                    <button
                        onClick={() => onSyncQbo(item.business_id)}
                        className="inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                    >
                        <RefreshCw className={`h-4 w-4 ${busyAction === `qbo:${item.business_id}` ? 'animate-spin' : ''}`} />
                        Sync QBO
                    </button>
                    <button
                        onClick={() => onSyncMappings(item.business_id)}
                        className="inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                    >
                        <Play className={`h-4 w-4 ${busyAction === `map:${item.business_id}` ? 'animate-spin' : ''}`} />
                        Map Accounts
                    </button>
                    <button
                        onClick={() => onRenormalize(item.business_id)}
                        className="inline-flex items-center gap-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm font-medium text-rose-700 hover:bg-rose-100"
                    >
                        <Play className={`h-4 w-4 ${busyAction === `reno:${item.business_id}` ? 'animate-spin' : ''}`} />
                        Renormalize
                    </button>
                    <button
                        onClick={() => onDownload(item)}
                        className="inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                    >
                        <Download className="h-4 w-4" />
                        Export
                    </button>
                </div>
            </div>

            <div className="mt-4 grid gap-4 md:grid-cols-4">
                <div className="rounded-lg bg-gray-50 p-3">
                    <div className="text-xs font-medium uppercase tracking-wide text-gray-500">Active Work</div>
                    <div className="mt-2 text-2xl font-semibold text-gray-900">{item.active_work_count}</div>
                    <div className="mt-1 text-xs text-gray-500">Last activity {formatLocalTimestamp(item.last_activity_at)}</div>
                </div>
                <div className="rounded-lg bg-gray-50 p-3">
                    <div className="text-xs font-medium uppercase tracking-wide text-gray-500">Pending AI</div>
                    <div className="mt-2 text-2xl font-semibold text-gray-900">{item.vendor_resolution.pending_ai_count}</div>
                    <div className="mt-1 text-xs text-gray-500">Needs review {item.vendor_resolution.needs_review_count}</div>
                </div>
                <div className="rounded-lg bg-gray-50 p-3">
                    <div className="text-xs font-medium uppercase tracking-wide text-gray-500">QBO Mapping</div>
                    <div className="mt-2 text-sm font-semibold text-gray-900">{item.qbo_account_mapping.detail || item.qbo_account_mapping.status}</div>
                    <div className="mt-1 text-xs text-gray-500">Updated {formatLocalTimestamp(item.qbo_account_mapping.updated_at)}</div>
                </div>
                <div className="rounded-lg bg-gray-50 p-3">
                    <div className="text-xs font-medium uppercase tracking-wide text-gray-500">Connections</div>
                    <div className="mt-2 text-sm text-gray-900">
                        QBO {item.connections.qbo_linked ? 'linked' : 'not linked'} · Stripe {item.connections.stripe_linked ? 'linked' : 'not linked'}
                    </div>
                    <div className="mt-1 text-xs text-gray-500">
                        Human verify {item.vendor_resolution.human_verify_count} · Deferred {item.vendor_resolution.deferred_history_count}
                    </div>
                </div>
            </div>

            <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-3">
                <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                    <div>
                        <div className="text-xs font-medium uppercase tracking-wide text-gray-500">QuickBooks Desktop Connector</div>
                        <div className="mt-2 flex flex-wrap items-center gap-2">
                            <StatusPill label="Status" status={connectorStatus} />
                            {connector.last_sync_status ? <StatusPill label="Last Sync" status={connector.last_sync_status} /> : null}
                            {connectorQueue > 0 ? <StatusPill label="Queue" status={`${connectorQueue} pending`} /> : null}
                        </div>
                    </div>
                    <div className="grid gap-x-5 gap-y-1 text-xs text-gray-500 sm:grid-cols-2 lg:grid-cols-4">
                        <div>
                            <span className="font-medium text-gray-700">Heartbeat</span>
                            <div>{formatLocalTimestamp(connector.last_heartbeat_at)}</div>
                        </div>
                        <div>
                            <span className="font-medium text-gray-700">Successful sync</span>
                            <div>{formatLocalTimestamp(connector.last_successful_sync_at)}</div>
                        </div>
                        <div>
                            <span className="font-medium text-gray-700">Machine</span>
                            <div>{connector.machine_name || '—'}</div>
                        </div>
                        <div>
                            <span className="font-medium text-gray-700">Company</span>
                            <div>{connector.company_file || '—'}</div>
                        </div>
                    </div>
                </div>
                {connector.last_error ? (
                    <div className="mt-3 rounded-md border border-red-100 bg-red-50 px-3 py-2 text-xs text-red-700">
                        {connector.last_error}
                    </div>
                ) : null}
            </div>

            {/* <div className="mt-4 grid gap-4 lg:grid-cols-2">
                <div>
                    <div className="mb-2 text-sm font-semibold text-gray-900">Recent Export Jobs</div>
                    <JobList jobs={item.jobs.export_recent} />
                </div>
                <div>
                    <div className="mb-2 text-sm font-semibold text-gray-900">Recent ETL Jobs</div>
                    <JobList jobs={item.jobs.etl_recent} />
                </div>
            </div> */}
        </div>
    );
};

const AdminOpsPage: React.FC = () => {
    const queryClient = useQueryClient();
    const { selectedBusinessId } = useAdminContext();
    const [days, setDays] = useState(3);
    const [includeIdle, setIncludeIdle] = useState(false);
    const [busyAction, setBusyAction] = useState<string | null>(null);
    const [renormalizeModalOpen, setRenormalizeModalOpen] = useState(false);
    const [selectedBusinessForRenormalize, setSelectedBusinessForRenormalize] = useState<{ id: string; name: string } | null>(null);
    const [exportModalOpen, setExportModalOpen] = useState(false);
    const [selectedBusinessForExport, setSelectedBusinessForExport] = useState<{ id: string; name: string } | null>(null);
    const scopedBusinessId = selectedBusinessId.trim() || undefined;

    const opsQuery = useQuery({
        queryKey: ['admin-ops-status', scopedBusinessId, days, includeIdle],
        queryFn: () => adminDataApi.getOpsStatus({
            business_id: scopedBusinessId,
            days,
            include_idle: includeIdle,
            per_business_job_limit: 5,
        }),
        refetchInterval: 10000,
    });

    const invalidateOps = () => {
        queryClient.invalidateQueries({ queryKey: ['admin-ops-status'] });
    };

    const qboSyncMutation = useMutation({
        mutationFn: (targetBusinessId: string) => adminDataApi.syncQbo({ business_id: targetBusinessId, type: 'all' }),
        onSuccess: () => invalidateOps(),
        onSettled: () => setBusyAction(null),
    });

    const qboMappingMutation = useMutation({
        mutationFn: (targetBusinessId: string) => vendorApi.syncQboAccountMappings(targetBusinessId, { forceRetry: true }),
        onSuccess: () => invalidateOps(),
        onSettled: () => setBusyAction(null),
    });

    const resolveMutation = useMutation({
        mutationFn: (targetBusinessId: string) => vendorApi.resolvePendingVendors({ businessId: targetBusinessId, batchSize: 10, excludeChecks: false }),
        onSuccess: () => invalidateOps(),
        onSettled: () => setBusyAction(null),
    });

    const renormalizeMutation = useMutation({
        mutationFn: ({ businessId: targetBusinessId, params }: { businessId: string; params: RenormalizeParams }) =>
            vendorApi.renormalizeVendors({
                ...params,
                businessId: targetBusinessId,
            }),
        onSuccess: () => {
            invalidateOps();
            setRenormalizeModalOpen(false);
            setSelectedBusinessForRenormalize(null);
        },
        onSettled: () => setBusyAction(null),
    });

    const handleAction = async (actionKey: string, runner: () => Promise<unknown>) => {
        try {
            setBusyAction(actionKey);
            await runner();
        } catch (error: any) {
            const detail = error?.response?.data?.detail;
            alert(typeof detail === 'string' ? detail : 'Action failed');
            setBusyAction(null);
        }
    };

    const summary = opsQuery.data?.summary;
    const items = opsQuery.data?.items || [];

    const handleOpenRenormalize = (item: AdminOpsBusinessStatus) => {
        setSelectedBusinessForRenormalize({
            id: item.business_id,
            name: item.business_name,
        });
        setRenormalizeModalOpen(true);
    };

    const handleOpenExport = (item: AdminOpsBusinessStatus) => {
        setSelectedBusinessForExport({
            id: item.business_id,
            name: item.business_name,
        });
        setExportModalOpen(true);
    };

    return (
        <div className="h-full overflow-y-auto p-6">
            <div className="mx-auto max-w-7xl pb-8">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
                    <div>
                        <h2 className="text-xl font-bold text-gray-900">Ops Monitor</h2>
                        <p className="mt-0.5 text-sm text-gray-500">Monitor onboarding and background work across businesses.</p>
                    </div>
                    <div className="flex flex-wrap items-center gap-3">
                        <select
                            value={days}
                            onChange={(event) => setDays(Number(event.target.value))}
                            className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                        >
                            <option value={1}>1 day</option>
                            <option value={3}>3 days</option>
                            <option value={7}>7 days</option>
                            <option value={14}>14 days</option>
                        </select>
                        <label className="flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 shadow-sm">
                            <input
                                type="checkbox"
                                checked={includeIdle}
                                onChange={(event) => setIncludeIdle(event.target.checked)}
                                className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                            />
                            Include idle businesses
                        </label>
                        <button
                            onClick={() => opsQuery.refetch()}
                            className="inline-flex items-center gap-2 rounded-md bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800"
                        >
                            <RefreshCw className={`h-4 w-4 ${opsQuery.isFetching ? 'animate-spin' : ''}`} />
                            Refresh
                        </button>
                    </div>
                </div>

                <div className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                        <div className="text-xs font-medium uppercase tracking-wide text-gray-500">Businesses in View</div>
                        <div className="mt-2 text-3xl font-semibold text-gray-900">{summary?.businesses ?? 0}</div>
                    </div>
                    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                        <div className="text-xs font-medium uppercase tracking-wide text-gray-500">QBO Mapping Running</div>
                        <div className="mt-2 text-3xl font-semibold text-gray-900">{summary?.qbo_mapping_running ?? 0}</div>
                    </div>
                    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                        <div className="text-xs font-medium uppercase tracking-wide text-gray-500">Active Jobs</div>
                        <div className="mt-2 text-3xl font-semibold text-gray-900">{(summary?.etl_running ?? 0) + (summary?.export_running ?? 0)}</div>
                        <div className="mt-1 text-xs text-gray-500">ETL {summary?.etl_running ?? 0} · Export {summary?.export_running ?? 0}</div>
                    </div>
                    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                        <div className="text-xs font-medium uppercase tracking-wide text-gray-500">Pending Vendor AI</div>
                        <div className="mt-2 text-3xl font-semibold text-gray-900">{summary?.pending_vendor_transactions ?? 0}</div>
                        <div className="mt-1 text-xs text-gray-500">{summary?.vendor_backlog_businesses ?? 0} businesses with backlog</div>
                    </div>
                </div>

                <div className="mt-6">
                    {opsQuery.isLoading ? (
                        <div className="flex items-center justify-center rounded-xl border border-gray-200 bg-white p-16 shadow-sm">
                            <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
                        </div>
                    ) : opsQuery.isError ? (
                        <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-sm text-red-700">
                            Failed to load admin ops status.
                        </div>
                    ) : !items.length ? (
                        <div className="rounded-xl border border-gray-200 bg-white p-10 text-sm text-gray-500 shadow-sm">
                            No active or recent background work found for the current filter.
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {items.map((item) => (
                                <BusinessCard
                                    key={item.business_id}
                                    item={item}
                                    busyAction={busyAction}
                                    onSyncQbo={(targetBusinessId) => handleAction(`qbo:${targetBusinessId}`, () => qboSyncMutation.mutateAsync(targetBusinessId))}
                                    onSyncMappings={(targetBusinessId) => handleAction(`map:${targetBusinessId}`, () => qboMappingMutation.mutateAsync(targetBusinessId))}
                                    onResolveVendors={(targetBusinessId) => handleAction(`resolve:${targetBusinessId}`, () => resolveMutation.mutateAsync(targetBusinessId))}
                                    onRenormalize={() => handleOpenRenormalize(item)}
                                    onDownload={handleOpenExport}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {selectedBusinessForRenormalize && (
                <RenormalizeModal
                    isOpen={renormalizeModalOpen}
                    onClose={() => {
                        if (renormalizeMutation.isPending) return;
                        setRenormalizeModalOpen(false);
                        setSelectedBusinessForRenormalize(null);
                    }}
                    onConfirm={(params) => {
                        const targetBusinessId = selectedBusinessForRenormalize.id;
                        setBusyAction(`reno:${targetBusinessId}`);
                        renormalizeMutation.mutate({
                            businessId: targetBusinessId,
                            params,
                        });
                    }}
                    isPending={renormalizeMutation.isPending}
                    businessName={selectedBusinessForRenormalize.name}
                />
            )}

            {selectedBusinessForExport && (
                <DataExportModal
                    isOpen={exportModalOpen}
                    onClose={() => {
                        setExportModalOpen(false);
                        setSelectedBusinessForExport(null);
                    }}
                    businessId={selectedBusinessForExport.id}
                    businessName={selectedBusinessForExport.name}
                />
            )}
        </div>
    );
};

export default AdminOpsPage;
